import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Link2, Package, Search, ShoppingBag, Store } from 'lucide-react';
import { connect } from 'react-redux';
import { StorefrontService } from '../../../core/services/storefront.service';
import { priceParts } from './price';
import '../../../modules/shared/theme/theme.scss';
import './storefront-public.scss';

interface Props { UserReducer: any; }

const PAGE_SIZE = 24;

const getVisitorId = (): string => {
    try { let id = localStorage.getItem('cc_sf_vid'); if (!id) { id = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('cc_sf_vid', id); } return id; } catch { return 'v_anon'; }
};
const getSessionId = (): string => {
    try { let id = sessionStorage.getItem('cc_sf_sid'); if (!id) { id = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('cc_sf_sid', id); } return id; } catch { return 's_anon'; }
};

const BrandStorefrontComponent: React.FC<Props> = ({ UserReducer }) => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [storefront, setStorefront] = useState<any>(null);
    const [allProducts, setAllProducts] = useState<any[]>([]);
    const [filtered, setFiltered] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [page, setPage] = useState(1);
    const [linkCopied, setLinkCopied] = useState<number | null>(null);

    const influencer_id = UserReducer?.influencer_id;
    const account_id = UserReducer?.account_id;

    useEffect(() => {
        if (!slug) return;
        StorefrontService.getPublicStorefront(slug)
            .then((res: any) => {
                if (res?.status === 1) {
                    setStorefront(res.data.storefront);
                    setAllProducts(res.data.products || []);
                    setFiltered(res.data.products || []);
                    // Fire the impression beacon (works for brand storefronts too).
                    StorefrontService.trackView({ slug, visitor_id: getVisitorId(), session_id: getSessionId() });
                } else {
                    setError('Storefront not found');
                }
            })
            .catch(() => setError('Failed to load storefront'))
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        let list = allProducts;
        if (category) list = list.filter(p => p.category === category);
        if (search) list = list.filter(p => p.product_title.toLowerCase().includes(search.toLowerCase()));
        setFiltered(list);
        setPage(1);
    }, [search, category, allProducts]);

    const categories = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleGetLink = async (product_id: number) => {
        if (!influencer_id) { navigate('/login'); return; }
        try {
            const res = await StorefrontService.generateAffiliateLink({ product_id, influencer_id, account_id });
            if (res?.data?.affiliate_url) {
                await navigator.clipboard.writeText(res.data.affiliate_url);
                setLinkCopied(product_id);
                setTimeout(() => setLinkCopied(null), 2500);
            }
        } catch {}
    };

    if (loading) return <div className="brd brd-pub"><div className="brd-center"><span className="brd-spinner" /></div></div>;
    if (error) return (
        <div className="brd brd-pub">
            <div className="brd-empty">
                <span className="brd-empty__icon"><Store size={28} /></span>
                <p className="brd-empty__title">{error}</p>
                <p className="brd-empty__body">Check the link and try again.</p>
            </div>
        </div>
    );

    const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
    const goToPage = (v: number) => { setPage(v); window.scrollTo(0, 0); };
    const openProduct = (id: number) => navigate(`/store/${slug}/product/${id}`);

    return (
        <div className="brd brd-pub">
            {/* Store header: banner, logo, name */}
            <header className="brd-pub-hero">
                <div className={`brd-pub-hero__banner${storefront.store_banner ? '' : ' brd-pub-hero__banner--empty'}`}>
                    {storefront.store_banner && <img src={storefront.store_banner} alt="" />}
                </div>
                <div className="brd-container brd-pub-hero__row">
                    {storefront.store_logo
                        ? <img src={storefront.store_logo} alt="" className="brd-pub-hero__logo" />
                        : <span className="brd-pub-hero__logo brd-pub-hero__logo--empty">{(storefront.store_name || '?').charAt(0).toUpperCase()}</span>}
                    <div className="brd-pub-hero__text">
                        <h1 className="brd-pub-hero__name">{storefront.store_name}</h1>
                        {storefront.store_description && <p className="brd-pub-hero__desc">{storefront.store_description}</p>}
                    </div>
                </div>
            </header>

            <main className="brd-container brd-pub__body">
                {/* Filters */}
                <div className="brd-pub-toolbar">
                    <label className="brd-search brd-pub-toolbar__search">
                        <Search size={16} />
                        <input value={search} placeholder="Search products" aria-label="Search products"
                            onChange={e => setSearch(e.target.value)} />
                    </label>
                    <span className="brd-pub-toolbar__count">{filtered.length} product{filtered.length === 1 ? '' : 's'}</span>
                </div>
                {categories.length > 0 && (
                    <div className="brd-chip-scroll brd-pub-cats" role="tablist" aria-label="Category">
                        {['', ...categories].map(c => (
                            <button key={c || 'all'} type="button" role="tab" aria-selected={category === c}
                                className={`brd-chip${category === c ? ' brd-chip--on' : ''}`} onClick={() => setCategory(c)}>
                                {c || 'All'}
                            </button>
                        ))}
                    </div>
                )}

                {/* Product grid */}
                {paginated.length === 0 ? (
                    <div className="brd-card brd-empty">
                        <span className="brd-empty__icon"><Package size={28} /></span>
                        <p className="brd-empty__title">{allProducts.length ? 'No products match' : 'No products yet'}</p>
                        <p className="brd-empty__body">
                            {allProducts.length ? 'Try a different search or category.' : 'This store hasn’t added any products. Check back soon.'}
                        </p>
                    </div>
                ) : (
                    <div className="brd-pub-grid">
                        {paginated.map(p => {
                            const price = priceParts(p);
                            return (
                                <article key={p.product_id} className="brd-card brd-pub-card">
                                    <button type="button" className="brd-pub-card__media" onClick={() => openProduct(p.product_id)} aria-label={`View ${p.product_title}`}>
                                        {p.primary_image ? <img src={p.primary_image} alt="" /> : <Package size={32} />}
                                        {price?.off && <span className="brd-pill brd-pill--success brd-pub-card__off">{price.off}% off</span>}
                                    </button>
                                    <div className="brd-pub-card__body">
                                        {p.category && <span className="brd-pub-card__cat">{p.category}</span>}
                                        <button type="button" className="brd-pub-card__title" title={p.product_title} onClick={() => openProduct(p.product_id)}>
                                            {p.product_title}
                                        </button>
                                        {price && (
                                            <p className="brd-pub-price">
                                                <span className="brd-pub-price__now">{price.now}</span>
                                                {price.was && <span className="brd-pub-price__was">{price.was}</span>}
                                            </p>
                                        )}
                                        {p.commission_percent > 0 && <p className="brd-pub-card__earn">Earn {p.commission_percent}% commission</p>}
                                    </div>
                                    <div className="brd-pub-card__actions">
                                        <button type="button" className="brd-btn brd-btn--outline brd-btn--sm" onClick={() => openProduct(p.product_id)}>View</button>
                                        {influencer_id ? (
                                            // Logged-in influencer: grab an affiliate link to promote.
                                            <button type="button" className={`brd-btn brd-btn--sm ${linkCopied === p.product_id ? 'brd-btn--success' : 'brd-btn--primary'}`}
                                                onClick={() => handleGetLink(p.product_id)}>
                                                {linkCopied === p.product_id ? <><Check size={14} /> Copied!</> : <><Link2 size={14} /> Get Link</>}
                                            </button>
                                        ) : (
                                            // Buyer: tracked outbound click to the brand's product page.
                                            <a className="brd-btn brd-btn--primary brd-btn--sm"
                                                href={p.click_token ? StorefrontService.clickUrl(p.click_token) : p.product_url}
                                                target="_blank" rel="noopener noreferrer">
                                                <ShoppingBag size={14} /> Shop
                                            </a>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}

                {pageCount > 1 && (
                    <nav className="brd-pub-pager" aria-label="Pages">
                        <button type="button" className="brd-icon-btn" disabled={page === 1} onClick={() => goToPage(page - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button>
                        <span>Page {page} of {pageCount}</span>
                        <button type="button" className="brd-icon-btn" disabled={page === pageCount} onClick={() => goToPage(page + 1)} aria-label="Next page"><ChevronRight size={18} /></button>
                    </nav>
                )}
            </main>
        </div>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer });
export const BrandStorefront = connect(mapStateToProps)(BrandStorefrontComponent);
